import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import QrScanner from 'qr-scanner';
import { useAppState } from '../src/contexts/AppStateContext';
import api from '../src/utils/axiosConfig';

const BadgeScanner = () => {
    const { currentEvent } = useAppState();
    const [searchParams, setSearchParams] = useSearchParams();
    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState('');
    const [note, setNote] = useState('');
    const [cameras, setCameras] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isScanning, setIsScanning] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [isMounted, setIsMounted] = useState(false);
    const [hasPermission, setHasPermission] = useState(null);

    const videoRef = useRef(null);
    const scannerRef = useRef(null);
    const lastScanTimeRef = useRef(0);

    const showMessage = (msg, type = 'success') => {
        setMessage(msg);
        setMessageType(type);
        setTimeout(() => {
            setMessage('');
            setMessageType('');
        }, type === 'error' ? 5000 : 3000);
    };

    // Initialize component
    useEffect(() => {
        setIsMounted(true);
        
        return () => {
            setIsMounted(false);
            stopScanning();
        };
    }, []);

    // Check camera support and permissions
    useEffect(() => {
        const checkCameraSupport = async () => {
            try {
                const hasCamera = await QrScanner.hasCamera();
                if (hasCamera) {
                    loadCameras();
                } else {
                    showMessage('No cameras found on this device', 'error');
                }
            } catch (error) {
                console.error('Camera support check failed:', error);
                showMessage('Failed to access camera. Please ensure HTTPS and camera permissions.', 'error');
            }
        };

        if (isMounted) {
            checkCameraSupport();
        }
    }, [isMounted]);

    const loadCameras = async () => {
        try {
            const cameraList = await QrScanner.listCameras(true);
            if (!isMounted) return;
            
            setCameras(cameraList);
            
            if (cameraList.length > 0) {
                // Prefer back camera for mobile
                const backCamera = cameraList.find(camera => 
                    camera.label.toLowerCase().includes('back') || 
                    camera.label.toLowerCase().includes('rear') ||
                    camera.label.toLowerCase().includes('environment')
                );
                setSelectedCamera(backCamera ? backCamera.id : cameraList[0].id);
            }
            setHasPermission(true);
        } catch (error) {
            console.error('Error loading cameras:', error);
            if (error.name === 'NotAllowedError') {
                setHasPermission(false);
                showMessage('Camera permission denied. Please allow camera access.', 'error');
            } else {
                showMessage('Failed to load cameras. Please check permissions.', 'error');
            }
        }
    };

    // Load zones when event changes
    useEffect(() => {
        const loadZones = async () => {
            if (!currentEvent?.id) {
                setZones([]);
                setSelectedZone('');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            
            try {
                const zonesResponse = await api.get(`/events/${currentEvent.id}/zones?active_only=true`);
                if (!isMounted) return;
                
                setZones(zonesResponse.data);
                
                // Handle zone from URL after loading zones
                const zoneFromUrl = searchParams.get('zone');
                if (zoneFromUrl) {
                    const zoneExists = zonesResponse.data.some(zone => zone.id.toString() === zoneFromUrl);
                    if (zoneExists) {
                        setSelectedZone(zoneFromUrl);
                    } else {
                        setSearchParams(prev => {
                            const newParams = new URLSearchParams(prev);
                            newParams.delete('zone');
                            return newParams;
                        }, { replace: true });
                        setSelectedZone('');
                    }
                } else {
                    setSelectedZone('');
                }
                
            } catch (error) {
                console.error('Error loading zones:', error);
                if (isMounted) {
                    showMessage('Failed to load zones', 'error');
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (isMounted) {
            loadZones();
        }
    }, [currentEvent?.id, isMounted]);

    const startScanning = async () => {
        if (!videoRef.current || isScanning || !selectedCamera) return;

        try {
            setIsScanning(true);
            
            // Create QR scanner instance
            scannerRef.current = new QrScanner(
                videoRef.current,
                result => {
                    if (!isMounted) return;
                    
                    const now = Date.now();
                    if (now - lastScanTimeRef.current > 2000) {
                        lastScanTimeRef.current = now;
                        handleSubmission(result.data);
                    }
                },
                {
                    onDecodeError: error => {
                        // Don't log decode errors as they're normal when no QR code is visible
                    },
                    preferredCamera: selectedCamera,
                    highlightScanRegion: true,
                    highlightCodeOutline: true,
                    maxScansPerSecond: 5,
                }
            );

            await scannerRef.current.start();
            showMessage('Scanner started successfully', 'success');

        } catch (error) {
            console.error('Error starting scanner:', error);
            setIsScanning(false);
            
            if (error.name === 'NotAllowedError') {
                showMessage('Camera permission denied. Please allow camera access.', 'error');
            } else if (error.name === 'NotFoundError') {
                showMessage('Camera not found. Please check camera selection.', 'error');
            } else if (error.name === 'NotSupportedError') {
                showMessage('Camera not supported. Please use HTTPS.', 'error');
            } else {
                showMessage('Failed to start camera. Please try again.', 'error');
            }
        }
    };

    const stopScanning = () => {
        if (scannerRef.current) {
            scannerRef.current.stop();
            scannerRef.current.destroy();
            scannerRef.current = null;
        }
        setIsScanning(false);
    };

    const handleSubmission = async (submissionId) => {
        if (!selectedZone) {
            showMessage('Please select a zone', 'error');
            return;
        }
        if (!currentEvent?.id) {
            showMessage('No event selected', 'error');
            return;
        }

        try {
            await api.post(`/events/${currentEvent.id}/checkins/`, {
                note: note,
                submission_id: submissionId,
                zone_id: selectedZone,
            });
            showMessage(`✅ Scanned QR Code: ${submissionId} successfully`, 'success');
        } catch (error) {
            console.error('Error submitting scan:', error);
            if (error.response?.data?.message) {
                showMessage(error.response.data.message, 'error');
            } else {
                showMessage('Failed to process scan', 'error');
            }
        }
    };

    const handleCameraChange = async (e) => {
        const newCameraId = e.target.value;
        setSelectedCamera(newCameraId);
        
        if (isScanning && scannerRef.current) {
            try {
                await scannerRef.current.setCamera(newCameraId);
                showMessage('Camera switched successfully', 'success');
            } catch (error) {
                console.error('Error switching camera:', error);
                showMessage('Failed to switch camera', 'error');
            }
        }
    };

    const handleZoneChange = (e) => {
        const zoneId = e.target.value;
        setSelectedZone(zoneId);
        
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (zoneId) {
                newParams.set('zone', zoneId);
            } else {
                newParams.delete('zone');
            }
            return newParams;
        }, { replace: true });
    };

    const requestPermissions = async () => {
        try {
            await loadCameras();
        } catch (error) {
            showMessage('Failed to get camera permissions', 'error');
        }
    };

    if (!currentEvent) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500">Please select an event to use the scanner</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex justify-center items-center p-8">
                <div className="text-gray-600">Loading scanner and event data...</div>
            </div>
        );
    }

    return (
        <div className="bg-white p-6">
            <h2 className="text-xl font-semibold mb-6">Ticket Scanner</h2>

            {/* HTTPS Warning */}
            {window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && (
                <div className="p-3 rounded-md mb-4 bg-orange-100 text-orange-800 border border-orange-200">
                    <strong>⚠️ Warning:</strong> Camera access requires HTTPS. Scanner may not work properly on HTTP.
                </div>
            )}

            {/* Permission Request */}
            {hasPermission === false && (
                <div className="p-4 rounded-md mb-4 bg-red-100 text-red-800 border border-red-200">
                    <p className="mb-3"><strong>Camera Permission Required</strong></p>
                    <p className="mb-3 text-sm">Please allow camera access to use the QR code scanner.</p>
                    <button
                        onClick={requestPermissions}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                        Grant Camera Permission
                    </button>
                </div>
            )}

            {message && (
                <div
                    className={`p-3 rounded-md mb-4 text-center ${messageType === 'success'
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                        }`}
                >
                    {message}
                </div>
            )}

            {/* Camera Selection */}
            {cameras.length > 0 && (
                <div className="mb-4">
                    <label htmlFor="camera-select" className="block text-sm font-medium text-gray-700 mb-1">
                        Select Camera
                    </label>
                    <select
                        id="camera-select"
                        value={selectedCamera}
                        onChange={handleCameraChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                    >
                        {cameras.map((camera) => (
                            <option key={camera.id} value={camera.id}>
                                {camera.label || `Camera ${camera.id}`}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            

            {/* Scanner Controls */}
            {hasPermission && cameras.length > 0 && (
                <div className="mb-6">
                    {!isScanning ? (
                        <button
                            onClick={startScanning}
                            disabled={!selectedZone || !selectedCamera}
                            className={`w-full px-4 py-3 rounded-md font-medium text-white ${
                                selectedZone && selectedCamera
                                    ? 'bg-green-500 hover:bg-green-600' 
                                    : 'bg-gray-400 cursor-not-allowed'
                            }`}
                        >
                            📷 Start QR Code Scanner
                        </button>
                    ) : (
                        <button
                            onClick={stopScanning}
                            className="w-full px-4 py-3 bg-red-500 text-white rounded-md hover:bg-red-600 font-medium"
                        >
                            ⏹️ Stop Scanner
                        </button>
                    )}
                </div>
            )}

            {/* Video Element */}
            <div className="mb-6">
                <video
                    ref={videoRef}
                    className="w-full border border-gray-300 rounded-md bg-gray-100"
                    style={{ 
                        maxHeight: '400px',
                        objectFit: 'cover'
                    }}
                    playsInline
                    muted
                />
                
                <div className="text-center mt-3">
                    {isScanning ? (
                        <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                            🔍 Scanning for QR codes...
                        </div>
                    ) : hasPermission === null ? (
                        <div className="text-gray-500">Checking camera permissions...</div>
                    ) : hasPermission === false ? (
                        <div className="text-red-600">Camera permission required</div>
                    ) : !selectedCamera ? (
                        <div className="text-gray-500">No cameras available</div>
                    ) : !selectedZone ? (
                        <div className="text-gray-500">Select a zone to start scanning</div>
                    ) : (
                        <div className="text-gray-500">Ready to scan - tap "Start QR Code Scanner"</div>
                    )}
                </div>
            </div>
            
            {/* Zone Selection */}
            <div className="mb-6">
                <label htmlFor="zone-select" className="block text-sm font-medium text-gray-700 mb-1">
                    Select Zone *
                </label>
                <select
                    id="zone-select"
                    value={selectedZone}
                    onChange={handleZoneChange}
                    disabled={zones.length === 0}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-base"
                >
                    <option value="">Select a zone</option>
                    {zones.map(zone => (
                        <option key={zone.id} value={zone.id}>
                            {zone.name} ({zone.code})
                        </option>
                    ))}
                </select>
                {!selectedZone && (
                    <p className="text-red-600 text-sm mt-1">⚠️ Please select a zone before starting the scanner</p>
                )}
            </div>
            {/* Note Input */}
            <div className="mb-6">
                <label htmlFor="note-input" className="block text-sm font-medium text-gray-700 mb-1">
                    Note (optional)
                </label>
                <input
                    id="note-input"
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Enter note"
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-2 focus:ring-indigo-500 text-base"
                />
            </div>

        </div>
    );
};

export default BadgeScanner;